// premium-portfolio: filterable case studies with real metrics
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Navigation } from '@/components/navigation';
import { Footer } from '@/components/footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, ExternalLink } from 'lucide-react';
import { useScrollReveal } from '@/hooks/use-scroll-reveal';
import { SEO } from '@/components/seo';

const filters = ['All', 'Web App', 'SaaS'];

const cases = [
  {
    id: 'propertyagent-crm',
    title: 'PropertyAgent-CRM',
    client: 'Internal Product',
    category: 'Web App',
    industry: 'Real Estate / PropTech',
    challenge: 'Real estate agents struggled with generic CRMs not designed for their unique workflow - from lead capture to closing deals. They needed mobile access for field work and AI-powered insights.',
    result: 'Built a complete AI-powered CRM with visual Kanban pipeline, commission forecasting, and mobile-first PWA design specifically for real estate professionals.',
    metrics: ['+30% closing rate', 'Mobile-First PWA', 'AI Pipeline Analysis'],
    image: '/images/portfolio/propertyagent-dashboard.png',
    logo: '/images/portfolio/propertyagent-logo.png',
    tags: ['Web App Development', 'AI Integration', 'PWA'],
    link: 'https://propertyagent-crm.com'
  },
  {
    id: 'selgr',
    title: 'SELGR',
    client: 'Internal Product',
    category: 'SaaS',
    industry: 'Sales Training / EdTech',
    challenge: 'Norwegian B2B salespeople lacked practical, hands-on training tools. Traditional sales courses were theoretical and didn\'t offer realistic practice scenarios with instant feedback.',
    result: 'Built an AI-powered sales training platform with interactive course modules, quizzes, and realistic AI roleplay simulations that adapt to each user\'s product, company, and skill level.',
    metrics: ['AI Roleplay', 'Personalized Learning', '10+ Course Modules'],
    image: '/images/portfolio/selgr-roleplay.png',
    logo: '/images/portfolio/selgr-logo.png',
    tags: ['SaaS Development', 'AI Integration', 'EdTech'],
    link: 'https://selgr.no'
  }
];

const Portfolio = () => {
  const [selectedFilter, setSelectedFilter] = useState('All');
  const { elementRef, getRevealStyle } = useScrollReveal();

  const filteredCases = selectedFilter === 'All' 
    ? cases 
    : cases.filter(case_study => case_study.category === selectedFilter);

  const collectionSchema = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    "name": "Portfolio",
    "description": "Web development case studies and success stories from AffinityAim",
    "url": "https://www.affinityaim.com/portfolio"
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.affinityaim.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Portfolio",
        "item": "https://www.affinityaim.com/portfolio"
      }
    ]
  };

  return (
    <div className="min-h-screen bg-background">
      <SEO
        title="Portfolio - Web Development Case Studies & Results | AffinityAim"
        description="Explore our web development portfolio. Real case studies with measurable results: 63% conversion increases, 28% revenue growth, and more."
        canonical="https://www.affinityaim.com/portfolio"
        keywords="web development portfolio, case studies, client success stories, web design examples"
        schema={[collectionSchema, breadcrumbSchema]}
      />
      <Navigation />

      <main id="main-content">
        {/* Hero */}
        <section className="section-spacing bg-background">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <div className="mb-16">
                <div className="micro-label mb-6">PORTFOLIO</div>
                <h1 className="font-display font-bold text-foreground mb-6" style={{ fontSize: '56px', lineHeight: '1.1', letterSpacing: '-0.01em' }}>
                  Results that speak
                  <br />
                  <span className="bg-gradient-primary bg-clip-text text-transparent">
                    for themselves
                  </span>
                </h1>
                <p className="text-body-lg text-muted-foreground max-w-paragraph mx-auto">
                  Real projects, measurable outcomes. See how we've helped companies grow through better digital experiences.
                </p>
              </div>

            </div>
          </div>
        </section>

      {/* Filters */}
      <section className="pb-8 bg-background">
        <div className="container mx-auto px-6">
          <div className="max-w-content mx-auto">
            <div className="flex flex-wrap justify-center gap-4 mb-12">
              {filters.map((filter) => (
                  <button
                    key={filter}
                    onClick={() => setSelectedFilter(filter)}
                    className={`px-6 py-2 rounded-full border transition-all duration-300 focus:ring-2 focus:ring-primary focus:ring-offset-2 focus:ring-offset-background tap-target ${
                      selectedFilter === filter
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'border-stroke-hairline text-muted-foreground hover:text-foreground hover:border-stroke-strong'
                    }`}
                    aria-pressed={selectedFilter === filter}
                  >
                    {filter}
                  </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="pb-20 bg-background">
        <div className="container mx-auto px-6">
          <div ref={elementRef} className="max-w-content-wide mx-auto">
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filteredCases.map((case_study, index) => (
                    <a 
                      key={case_study.id}
                      href={case_study.link || '/contact'}
                      target={case_study.link ? '_blank' : '_self'}
                      rel={case_study.link ? 'noopener noreferrer' : undefined}
                      className="group hover-lift tap-target"
                      style={getRevealStyle(index)}
                      aria-label={case_study.link ? `Visit ${case_study.title} website` : `Contact us about ${case_study.title}`}
                    >
                      <div className="card-matte overflow-hidden">
                        
                        {/* Image */}
                        <div className="aspect-video bg-background-secondary relative overflow-hidden">
                          <img 
                            src={case_study.image} 
                            alt={`${case_study.title} project preview`}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            loading="lazy"
                            width="600"
                            height="400"
                          />
                          <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                          {case_study.logo && (
                            <div className="absolute top-4 left-4 bg-white rounded-lg p-2">
                              <img 
                                src={case_study.logo} 
                                alt={`${case_study.title} logo`}
                                className="h-6 w-auto"
                              />
                            </div>
                          )}
                        </div>

                        {/* Content */}
                        <div className="p-8">
                          
                          {/* Header */}
                          <div className="mb-4">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="text-xs font-semibold text-primary uppercase tracking-wide">
                                {case_study.category}
                              </span>
                              <span className="text-xs text-muted-foreground">
                                {case_study.industry}
                              </span>
                            </div>
                            <h3 className="font-display font-semibold text-foreground mb-2 group-hover:text-primary transition-colors tile-text" style={{ fontSize: '20px', lineHeight: '1.3' }}>
                              {case_study.title}
                            </h3>
                            <p className="text-sm text-muted-foreground mb-4">
                              {case_study.client}
                            </p>
                          </div>

                          {/* Challenge & Result */}
                          <div className="mb-6">
                            <p className="text-muted-foreground text-sm leading-relaxed mb-3 tile-text">
                              <strong className="text-foreground">Challenge:</strong> {case_study.challenge}
                            </p>
                            <p className="text-muted-foreground text-sm leading-relaxed tile-text">
                              <strong className="text-foreground">Solution:</strong> {case_study.result}
                            </p>
                          </div>

                          {/* Metrics */}
                          <div className="mb-6">
                            <div className="flex flex-wrap gap-2">
                              {case_study.metrics.slice(0, 1).map((metric, idx) => (
                                <span 
                                  key={idx}
                                  className="inline-flex items-center px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium"
                                >
                                  {metric}
                                </span>
                              ))}
                              {case_study.metrics.slice(1).map((metric, idx) => (
                                <span 
                                  key={idx + 1}
                                  className="inline-flex items-center px-3 py-1 rounded-full bg-surface border border-stroke-hairline text-muted-foreground text-xs font-medium"
                                >
                                  {metric}
                                </span>
                              ))}
                            </div>
                          </div>

                          {/* Tags & CTA */}
                          <div className="flex items-center justify-between">
                            <div className="flex flex-wrap gap-1">
                              {case_study.tags.slice(0, 2).map((tag, idx) => (
                                <span 
                                  key={idx}
                                  className="text-xs text-muted-foreground bg-background-secondary px-2 py-1 rounded"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                            {case_study.link ? (
                              <div className="flex items-center gap-2 text-primary group-hover:gap-3 transition-all duration-300">
                                <span className="text-xs font-medium">View live</span>
                                <ExternalLink className="w-4 h-4" />
                              </div>
                            ) : (
                              <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary group-hover:translate-x-1 transition-all duration-300" />
                            )}
                          </div>

                        </div>
                      </div>
                    </a>
              ))}
            </div>

          </div>
        </div>
      </section>

        {/* CTA Section */}
        <section className="py-20 bg-background-secondary">
          <div className="container mx-auto px-6">
            <div className="max-w-content mx-auto text-center">
              
              <h2 className="font-display font-semibold text-foreground mb-6" style={{ fontSize: '40px', lineHeight: '1.1' }}>
                Ready to create your success story?
              </h2>
              <p className="text-muted-foreground mb-8 max-w-paragraph mx-auto">
                Let's discuss your project and see how we can help you achieve similar results.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/contact">
                  <Button variant="default" size="lg" className="group bg-primary hover:bg-primary/90 text-primary-foreground tap-target">
                    Start your project
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <Link to="/services">
                  <Button variant="ghost" size="lg" className="text-muted-foreground hover:text-foreground tap-target">
                    View services
                  </Button>
                </Link>
              </div>

            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Portfolio;